import React from "react";
import { useConfigurationOptionsContext } from "../../../../../app/providers/DoorOptionsProvider";
import {
  DoorGlassMaterial,
  getFramePositions,
  getFrameScaling,
} from "../../../../../shared/utils/door";
import { frameSettings } from "shared/utils/doorConfigurations";

interface FrameProps extends frameSettings {
  doorNodes: any;
  materials: DoorGlassMaterial;
  index: number;
}

const Frame: React.FC<FrameProps> = ({
  doorNodes,
  materials,
  index,
  ...frameSettings
}: FrameProps) => {
  const {
    getConfigurationHeight,
    getConfigurationWidth,
    getConfigurationColor,
  } = useConfigurationOptionsContext();

  const height = getConfigurationHeight(index);
  const width = getConfigurationWidth(index);

  const positions = getFramePositions(width, height);
  const scaling = getFrameScaling(width, height);

  const yPositionSides = -0.01 * (-1 * (1 - scaling.vertical.y));

  // Round down to the nearest hundred
  const roundedWidth = Math.floor(width / 100) * 100;
  // Subtract 1200 with a minimum of 0 and divide by 1000
  // The result will be the added offset for the misc_0 and misc_1 (the hinges)
  // Value will be between 0 and 0.4
  const xMisc = Math.min(400, Math.max(0, roundedWidth - 1200)) / 1000;

  return (
    <group dispose={null}>
      <mesh
        geometry={doorNodes["frame_top"].geometry}
        material={materials[getConfigurationColor()]}
        scale={[
          scaling.horizontal.x,
          frameSettings?.frameTop?.scaleY ?? scaling.horizontal.y,
          scaling.horizontal.z,
        ]}
        position={[
          positions.top.x,
          positions.top.y + (frameSettings?.frameTop?.shiftY ?? 0),
          positions.top.z + (frameSettings?.frameTop?.shiftZ ?? 0),
        ]}
      ></mesh>
      <mesh
        geometry={doorNodes["frame_bottom"].geometry}
        material={materials[getConfigurationColor()]}
        scale={scaling.horizontal}
        position={[
          positions.bottom.x,
          positions.bottom.y + (frameSettings?.frameBottom?.shiftY ?? 0),
          positions.bottom.z,
        ]}
      ></mesh>

      <mesh
        geometry={doorNodes["frame_left"].geometry}
        material={materials[getConfigurationColor()]}
        scale={[
          scaling.vertical.x + (frameSettings?.frameLeft?.scaleX ?? 0),
          scaling.vertical.y + (frameSettings?.frameLeft?.scaleY ?? 0),
          scaling.vertical.z,
        ]}
        position={[
          positions.left.x + (frameSettings?.frameLeft?.shiftX ?? 0),
          yPositionSides + (frameSettings?.frameLeft?.shiftY ?? 0),
          0,
        ]}
      ></mesh>

      <mesh
        geometry={doorNodes["frame_right"].geometry}
        material={materials[getConfigurationColor()]}
        scale={[
          scaling.vertical.x + (frameSettings?.frameRight?.scaleX ?? 0),
          scaling.vertical.y + (frameSettings?.frameRight?.scaleY ?? 0),
          scaling.vertical.z,
        ]}
        position={[
          positions.right.x + (frameSettings?.frameRight?.shiftX ?? 0),
          yPositionSides + (frameSettings?.frameRight?.shiftY ?? 0),
          0,
        ]}
      ></mesh>

      <mesh
        geometry={doorNodes["misc_0"].geometry}
        rotation={[0, frameSettings?.misc0?.rotateY ?? 0, 0]}
        material={materials[getConfigurationColor()]}
        position={[
          positions.right.x - xMisc,
          positions.top.y + (frameSettings?.frameTop?.shiftY ?? 0),
          0,
        ]}
      ></mesh>
      <mesh
        geometry={doorNodes["misc_1"].geometry}
        rotation={[0, frameSettings?.misc1?.rotateY ?? 0, 0]}
        material={materials[getConfigurationColor()]}
        position={[positions.right.x - xMisc, positions.bottom.y, 0]}
      ></mesh>
    </group>
  );
};

export default Frame;
